import { Link } from "react-router-dom";
import { Home as HomeIcon, Hammer, ShieldCheck, Award, ArrowRight } from "lucide-react";
import { BUSINESS, ASSETS, HIGHLIGHTS } from "@/data";

const ICONS = { Home: HomeIcon, Hammer, ShieldCheck, Award };

export default function Home() {
  return (
    <main id="home" data-testid="home-page">
      {/* ===== HERO ===== */}
      <section
        className="relative pt-28 lg:pt-32"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(20,17,13,0.92) 0%, rgba(20,17,13,0.72) 45%, rgba(20,17,13,0.55) 100%), url(${ASSETS.heroTools})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center min-h-[78vh] pb-16">
          {/* Logo emblem */}
          <div className="flex justify-center lg:justify-start fade-up">
            <img
              src={ASSETS.logo}
              alt="L3 Construction emblem"
              data-testid="hero-logo"
              className="w-64 sm:w-80 lg:w-[26rem] object-contain"
              style={{
                WebkitMaskImage: "radial-gradient(circle at center, #000 80%, transparent 92%)",
                maskImage: "radial-gradient(circle at center, #000 80%, transparent 92%)",
                filter: "drop-shadow(0 16px 40px rgba(0,0,0,0.7))",
              }}
            />
          </div>

          {/* Copy */}
          <div className="fade-up" style={{ animationDelay: "0.15s" }}>
            <p className="font-cond tracking-[0.3em] text-gold text-sm mb-5">BUILT WITH INTEGRITY</p>
            <h1 className="font-display heading-distress text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
              BUILDING HOMES.<br />BUILDING TRUST.
            </h1>
            <div className="w-20 h-1 bg-gold mb-6" style={{ backgroundColor: "var(--l3-gold)" }} />
            <p className="text-white/75 text-lg leading-relaxed max-w-xl mb-9">
              At L3 Construction, we specialize in custom homebuilding with unmatched craftsmanship,
              attention to detail, and a commitment to quality that stands the test of time — backed by
              owners with over {BUSINESS.experienceYears} years of combined experience.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/projects"
                data-testid="hero-view-work-btn"
                className="inline-flex items-center gap-2 font-cond tracking-widest px-9 py-4 btn-gold rounded-sm"
              >
                VIEW OUR WORK <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="inline-flex items-center font-cond tracking-widest px-9 py-4 btn-outline-gold rounded-sm"
              >
                GET A QUOTE
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURE STRIP ===== */}
      <section data-testid="highlights-strip" className="bg-cream" style={{ backgroundColor: "var(--l3-cream)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid gap-y-12 gap-x-6 sm:grid-cols-2 lg:grid-cols-4">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = ICONS[h.icon] || HomeIcon;
              return (
                <div
                  key={h.title}
                  data-testid={`highlight-${i}`}
                  className={`flex gap-5 ${i !== 0 ? "lg:border-l lg:border-[#c9bb9f] lg:pl-6" : ""}`}
                >
                  <Icon size={42} strokeWidth={1.5} style={{ color: "var(--l3-gold)" }} className="shrink-0" />
                  <div>
                    <h3 className="font-cond font-semibold tracking-wide text-lg mb-2" style={{ color: "var(--l3-wood-dark)" }}>
                      {h.title.toUpperCase()}
                    </h3>
                    <p className="text-[15px] leading-relaxed" style={{ color: "#5b4f3d" }}>
                      {h.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== EXPERIENCE BAND ===== */}
      <section className="bg-wood-dark" style={{ backgroundColor: "var(--l3-wood-dark)" }}>
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <p className="font-cond tracking-[0.3em] text-gold text-sm mb-4">{BUSINESS.serviceArea.toUpperCase()}</p>
          <h2 className="font-display text-4xl sm:text-5xl heading-distress mb-4">
            60+ YEARS OF COMBINED EXPERIENCE
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-lg leading-relaxed">
            Our owners bring more than six decades of combined homebuilding experience to every
            project — because relationships matter as much as the structures we raise. Let's build
            something that lasts.
          </p>
          <a
            href={BUSINESS.phoneHref}
            data-testid="experience-call-btn"
            className="inline-flex items-center gap-2 mt-8 font-cond tracking-widest px-9 py-4 btn-gold rounded-sm"
          >
            CALL {BUSINESS.phone}
          </a>
        </div>
      </section>
    </main>
  );
}
